import validator from "validator";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  AveragePositionIcon,
  ImprovedIcon,
  Top3Icon,
  Top10Icon,
  Top30Icon,
  Top100Icon,
} from "../icons/SVGIcons";
import { COUNTRIES } from "./countriesList";

export const getToken = () => {
  return Cookies.get("token");
};

export const getAssignedkeywords = () => {
  return Cookies.get("assignedKeywords");
};

export const guideCompleted = () => {
  return Cookies.get("isGuideCompleted") === "false";
};

export function isValidEmail(email) {
  return validator.isEmail(email);
}

// Validate Card Number
export const isValidCardNumber = (cardNumber) => {
  // Implement your card number validation logic here
  // For example, check if it's a valid numeric value
  return /^\d+$/.test(cardNumber);
};

// Validate Expiry (Assuming MMYY format)
export const isValidExpiry = (expiry) => {
  // Implement your expiry validation logic here
  // For example, check if it's a valid numeric value and has a length of 4
  return /^\d{4}$/.test(expiry);
};

// Validate CVV
export const isValidCVV = (cvv) => {
  // Implement your CVV validation logic here
  // For example, check if it's a valid numeric value and has a length of 3
  return /^\d{3}$/.test(cvv);
};

export const toastSuccess = (message) => {
  toast(message, {
    position: "bottom-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  });
};

export const toastError = (message) => {
  toast.error(message, {
    position: "bottom-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  });
};

export const isLoggedIn = () => {
  const token = getToken();
  const isPaymentCompleted = Cookies.get("isPaymentCompleted");

  if (token && isPaymentCompleted === "true") {
    return true;
  } else {
    return false;
  }
};

export const formattedDate = (row) => {
  const date = new Date(row.date);
  const now = new Date();
  const diff = now - date;
  const options = {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  };

  // Options for formatting only the date
  const dateOnlyOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  };

  // Calculate difference in milliseconds
  const minute = 60 * 1000;
  const hour = minute * 60;
  const day = hour * 24;
  const week = day * 7;

  // Check for relative time
  if (diff < minute) {
    return "Just now";
  } else if (diff < 2 * minute) {
    return "1 minute ago";
  } else if (diff < hour) {
    return `${Math.floor(diff / minute)} minutes ago`;
  } else if (diff < 2 * hour) {
    return "1 hour ago";
  } else if (diff < day) {
    return `${Math.floor(diff / hour)} hours ago`;
  } else if (diff < 2 * day) {
    return "1 day ago";
  } else if (diff < week) {
    return `${Math.floor(diff / day)} days ago`;
  } else {
    // If more than a week ago, return formatted date
    return new Intl.DateTimeFormat("en-US", dateOnlyOptions).format(date);
  }
};

export function capitalizeFirstLetter(string) {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const getCountriesEmoji = (name) => {
  const country = COUNTRIES.find((country) => country.name === name);
  return country ? country.emoji : null;
};

export const getCountriesCode = (name) => {
  const country = COUNTRIES.find((country) => country.name === name);
  return country ? country.code : name;
};

export const getFormattedCountryWithDomain = (name) => {
  const country = COUNTRIES.find((country) => country.name === name);
  if (!country) return name;
  return `${country.name} - (${country.domain?.google || "google.com"})`;
};

export const calculatePercentage = (left_refresh, monthly_refresh) => {
  if (monthly_refresh === 0) {
    return 0;
  }
  return Math.round((left_refresh / monthly_refresh) * 100);
};

export const generateDynamicSummary = (data) => {
  let summaryData = [
    {
      mainText: "Average Position",
      description: `vs ${data?.average_position?.previous_avg} (change ${data?.average_position?.change})`, //"vs 4.17 (change -0.84)",
      icon: <AveragePositionIcon />,
      color: "#049A55",
      average: data?.average_position?.current_avg,
    },
    {
      mainText: "Improved",
      description: `vs ${data?.improved_keywords?.previous_count} (change ${data?.improved_keywords?.change})`,
      icon: <ImprovedIcon />,
      color: "rgba(32, 32, 32, 0.60)",
      average: data?.up_keys,
    },
    {
      mainText: "Top 3",
      description: `vs ${data?.top_3_keywords?.previous_count} (change ${data?.top_3_keywords?.change})`, // "vs 4 (change -1.00)",
      icon: <Top3Icon />,
      color: "#049A55",
      average: data?.top_3_keywords?.current_count,
    },
    {
      mainText: "Top 10",
      description: `vs ${data?.top_10_keywords?.previous_count} (change ${data?.top_10_keywords?.change})`, // "vs 6 (change 0.00)",
      icon: <Top10Icon />,
      color: "rgba(32, 32, 32, 0.60)",
      average: data?.top_10_keywords?.current_count,
    },
    {
      mainText: "Top 30",
      description: `vs ${data?.top_30_keywords?.previous_count} (change ${data?.top_30_keywords?.change})`, // "vs 4.17 (change 0.00)",
      icon: <Top30Icon />,
      color: "rgba(32, 32, 32, 0.60)",
      average: data?.top_30_keywords?.current_count,
    },
    {
      mainText: "Top 100",
      description: `vs ${data?.top_100_keywords?.previous_count} (change ${data?.top_100_keywords?.change})`, // "vs 4.17 (change 0.00)",
      icon: <Top100Icon />,
      color: "rgba(32, 32, 32, 0.60)",
      average: data?.top_100_keywords?.current_count,
    },
  ];
  return summaryData;
};

export const clearCookies = () => {
  Cookies.remove("token");
  Cookies.remove("isPaymentCompleted");
  Cookies.remove("csrftoken");
  Cookies.remove("freeTrialEndDays");
  Cookies.remove("sessionid");
  Cookies.remove("profileURL");
  Cookies.remove("isGuideCompleted");
  Cookies.remove("ASSIGNED_KEYWORDS");
};

export const formatVolume = (volume) => {
  if (typeof volume !== "number") {
    return volume; // If the volume is not a number, return it as is
  }

  if (volume >= 1e6) {
    return (volume / 1e6).toFixed(1) + "M"; // Convert to millions with one decimal place
  }

  if (volume >= 1e3) {
    return (volume / 1e3).toFixed(1) + "K"; // Convert to thousands with one decimal place
  }

  return volume.toString(); // Return the number as a string if less than 1000
};


export const getRegionByCountry = (name) => {
  const country = COUNTRIES.find((country) => country.name === name);
  return country ? country.domain?.google : "google.com";
}


export const validatePassword = (password) => {
  // Initialize the error message
  let passwordErr = "";

  // Check if the password is empty
  if (password.length <= 0) {
    passwordErr = "Password cannot be empty";
    return passwordErr;
  }
  
  // Check if the password is at least 8 characters long
  if (password.length < 8) {
    passwordErr = "Password must be at least 8 characters long";
    return passwordErr;
  }
  
  // Check if the password contains at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    passwordErr = "Password must contain at least one uppercase letter";
    return passwordErr;
  }
  // If all validations pass, return an empty error message
  return passwordErr;
}

export const urlOrkeyword = (heading) => {
  if (["1", "6", "7"].includes(heading)) {
    // if heading has url 
    return "URL"
    
  }else if (["2", "4", "5", "8"].includes(heading)){
    // if heading has Keyword
    return "KEYWORD"
  }else if(["3"].includes(heading)){
    return "BOTH"
  }
  return "URL"
}

export const isURL = (string) => {
  try {
      // Ensure the URL starts with a scheme; add 'http://' if missing
      if (!string.startsWith('http://') && !string.startsWith('https://')) {
          string = `http://${string}`;
      }
      new URL(string); // Attempt to create a URL object
      return true; // Valid URL
  } catch {
      return false; // Invalid URL
  }
};

export const isKeyword = (string) => {
  try{
    new URL(string)
    return false
  }catch {
    return true
  }
}

export const generateTitle = () => {
  const today = new Date();
  const formattedDate = today.toISOString().split('T')[0].split('-').reverse().join('-'); // Format: DD-MM-YYYY
  const formattedTime = today.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }); // Format: HH:mm:ss

  return `Draft ${formattedDate} ${formattedTime}`;
}

export const processContent = (content) => {
  // Trim the content and remove the surrounding backticks
  return content.trim().replace(/^```|```$/g, '');
};

export const setSubscriptionData = (data) => {
  Cookies.set("subscriptionData", JSON.stringify(data))
}

export const getSubscriptionData = () => {
  return JSON.parse(Cookies.get("subscriptionData") || JSON.stringify({
      subscriptionType: "Monthly",
      keyword: 50,
      plan: "Starter"
  }))
}

export const clearSubscriptionData = () => {
  Cookies.remove("subscriptionData")
}

export const freeTrialSubsdata = (router) => {
  const data = {
    subscriptionType: "Monthly",
    keyword: 50,
    plan: "Starter",
  };
  setSubscriptionData(data);
  router.push("/sign-up");
};

export const getPricingType = (keyword) => {
  if (keyword === 50){
    return "Starter Plan"
  }else if(keyword === 100){
    return "Pro Plan"
  }else if (keyword > 100 && keyword <= 500){
    return "Growth Plan"
  }else if (keyword > 500 && keyword <= 1000){
    return "Agency Plan"
  }else{
    return "Agency Plan"
  }
}

/**
 * Track a Google Analytics event
 * @param {string} eventName - The name of the event to track
 * @param {Object} eventParams - The parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window === 'undefined') return;
  
  try {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
      console.log(`Event tracked: ${eventName}`, eventParams);
    } else if (window.trackEvent) {
      window.trackEvent(eventName, eventParams);
    } else {
      console.warn('Google Analytics not available for tracking event:', eventName);
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};